import React, { useMemo } from 'react';
import { Checkbox, Button, Box, Typography } from 'extended-oxygen-elements';
import {
  DutyConfiguration,
  getDutyLabel,
  formatDutyTimePeriods,
  formatDutyRequiredPeople,
  formatDutyMaxPeople,
} from '../types';

// Inline constants to avoid module resolution issues
const COLORS = {
  primary: '#000000',
  secondary: '#545454',
  background: '#ffffff',
  borderLight: 'rgba(0, 0, 0, 0.08)',
} as const;

const CHECKBOX_STYLES = {
  '& .MuiSvgIcon-root': { fontSize: 18 },
  padding: 0,
  '&.Mui-disabled': {
    color: 'rgba(0, 0, 0, 0.38)',
  },
  '&.Mui-checked.Mui-disabled': {
    color: 'rgba(25, 118, 210, 0.5)',
  },
} as const;

type DutyRowProps = {
  duty: DutyConfiguration;
  onConfigure: (dutyType: string) => void;
  icon?: React.ComponentType<{ sx?: any }>;
};

export const DutyRow = React.memo(function DutyRow({ duty, onConfigure, icon: Icon }: DutyRowProps) {
  const formattedValues = useMemo(() => ({
    timePeriods: formatDutyTimePeriods(duty),
    requiredPeople: formatDutyRequiredPeople(duty),
    maxPeople: formatDutyMaxPeople(duty),
    label: getDutyLabel(duty),
  }), [duty]);

  const hasTimePeriods = duty.is_enabled && duty.time_periods && duty.time_periods.length > 0;

  return (
    <>
      {/* Desktop Layout - Table Row */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          gap: '12px',
          alignItems: 'center',
          width: '100%',
          backgroundColor: COLORS.background,
          padding: '12px 0',
        }}
        role="row"
        aria-label={`${formattedValues.label} duty`}
      >
        {/* Checkbox */}
        <Box sx={{ flexShrink: 0 }} role="cell">
          <Checkbox
            checked={duty.is_enabled}
            disabled
            size="small"
            sx={CHECKBOX_STYLES}
            inputProps={{ 'aria-label': `${formattedValues.label} is ${duty.is_enabled ? 'enabled' : 'disabled'}` }}
          />
        </Box>

        {/* Icon + Duty Name */}
        <Box 
          role="cell"
          sx={{ 
            flex: '1 0 0', 
            minWidth: 0, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
          }}
        >
          {Icon && <Icon sx={{ fontSize: 24, color: COLORS.secondary }} aria-hidden="true" />}
          <Typography variant="body2" sx={{ fontWeight: 400, color: COLORS.primary }}>
            {formattedValues.label}
          </Typography>
        </Box>

        {/* Times / No. People */}
        <Box role="cell" sx={{ width: '180px', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: COLORS.primary }}>
            {formattedValues.timePeriods}
          </Typography>
          {hasTimePeriods && (
            <Typography variant="caption" sx={{ color: COLORS.secondary }}>
              {formattedValues.requiredPeople}
            </Typography>
          )}
        </Box>

        {/* No. People */}
        <Box role="cell" sx={{ width: '100px', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: COLORS.primary }}>{formattedValues.maxPeople}</Typography>
        </Box>

        {/* Configure Button */}
        <Box role="cell" sx={{ width: '120px', flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onConfigure(duty.duty_type)}
            disabled={!duty.is_enabled}
            aria-label={`Configure ${formattedValues.label}`}
          >
            Configure
          </Button>
        </Box>
      </Box>

      {/* Mobile Layout - Card Style */}
      <Box
        sx={{
          display: { xs: 'block', sm: 'none' },
          backgroundColor: COLORS.background,
          padding: '12px 0',
          borderBottom: `1px solid ${COLORS.borderLight}`,
        }}
        role="row"
        aria-label={`${formattedValues.label} duty`}
      >
        {/* Row 1: Checkbox + Icon + Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1 }}>
          <Checkbox
            checked={duty.is_enabled}
            disabled
            size="small"
            sx={CHECKBOX_STYLES}
            inputProps={{ 'aria-label': `${formattedValues.label} is ${duty.is_enabled ? 'enabled' : 'disabled'}` }}
          />
          {Icon && <Icon sx={{ fontSize: 20, color: COLORS.secondary }} aria-hidden="true" />}
          <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, color: COLORS.primary }}>
            {formattedValues.label}
          </Typography>
        </Box>

        {/* Row 2: Times and People info */}
        <Box sx={{ display: 'flex', gap: '16px', pl: '34px', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: COLORS.secondary, display: 'block' }}>
              Times / No. People
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: COLORS.primary }}>
              {formattedValues.timePeriods}
            </Typography>
            {hasTimePeriods && (
              <Typography variant="caption" sx={{ color: COLORS.secondary }}>
                {formattedValues.requiredPeople}
              </Typography>
            )}
          </Box>
          <Box sx={{ minWidth: '80px' }}>
            <Typography variant="caption" sx={{ color: COLORS.secondary, display: 'block' }}>
              No. People
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.primary }}>{formattedValues.maxPeople}</Typography>
          </Box>
        </Box>

        {/* Row 3: Configure Button */}
        <Box sx={{ pl: '34px' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onConfigure(duty.duty_type)}
            disabled={!duty.is_enabled}
            aria-label={`Configure ${formattedValues.label}`}
            sx={{ minWidth: '100px' }}
          >
            Configure
          </Button>
        </Box>
      </Box>
    </>
  );
});

export default DutyRow;
